import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Empty, Select, Space, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExportOutlined, FileSearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fct } from '../../fct/tokens/tokens';
import { FctEstado } from '../../fct/react';
import BarraLista, { BotonActualizar } from './BarraLista';
import { EncuestaF4, EncuestaRegistrada, FichaF4, SinEncuestaF4, getEncuestasF4, getF4SinEncuesta } from '../../api/cxPanelApi';
import { RespuestasModal } from './RegistradasTab';
import { BuscadorCliente } from './CampanaSemanaTab';
import VisibilidadF4Switch from './VisibilidadF4Switch';
import FichaF4Drawer, { BotonFichaF4 } from './FichaF4Drawer';
import { coincideBusqueda, estadoMystery, mensajeError, urlPortalMystery } from './panelCx';

const { Text } = Typography;

/**
 * Filtro «Estado»: las mismas opciones que la pantalla «Operaciones F4 - Encuestas» de AuraQuantic
 * («[sin encuesta]», estado de la encuesta y «no visible Mystery»), más «Visible para Mystery» y «Quitadas a mano».
 * «Sin encuesta» son los clientes de una operación que está hoy en Fase 4 y aún no tienen F4.
 */
type FiltroF4 = 'todas' | 'sinencuesta' | 'pendiente' | 'iniciada' | 'con' | 'sin' | 'visibles' | 'ocultas' | 'manual';

interface OpcionFiltroF4 {
  valor: FiltroF4;
  texto: string;
  color: string;
  cumple: (f: EncuestaF4) => boolean;
}

const POR_ESTADO: OpcionFiltroF4[] = [
  { valor: 'pendiente', texto: 'Pendiente', color: fct.estado.aviso.linea, cumple: (f) => f.estadoCod === 953 },
  { valor: 'iniciada', texto: 'Iniciada', color: fct.estado.proceso.linea, cumple: (f) => f.estadoCod === 954 },
  { valor: 'con', texto: 'Terminada con respuesta', color: fct.estado.exito.linea, cumple: (f) => f.estadoCod === 956 },
  {
    valor: 'sin',
    texto: 'Terminada sin respuesta',
    color: fct.tinta['500'],
    cumple: (f) => f.estadoCod === 955 || f.estadoCod === 957,
  },
];

const POR_VISIBILIDAD: OpcionFiltroF4[] = [
  { valor: 'visibles', texto: 'Visible para Mystery', color: fct.estado.exito.linea, cumple: (f) => f.visibleMystery },
  { valor: 'ocultas', texto: 'No visible Mystery', color: fct.tinta['500'], cumple: (f) => !f.visibleMystery },
  {
    valor: 'manual',
    texto: 'Quitadas a mano',
    color: fct.estado.aviso.linea,
    cumple: (f) => f.quitadaManual && !f.visibleMystery,
  },
];

const FILTROS_F4 = [...POR_ESTADO, ...POR_VISIBILIDAD];

/** Texto de una opción con su contador (también en la opción elegida). */
const etiquetaFiltro = (texto: string, n: number, color: string) => (
  <Space size={8} align="center">
    <span>{texto}</span>
    <Badge count={n} showZero overflowCount={9999} style={{ backgroundColor: color }} />
  </Space>
);

/** Lo que se pinta de cada cliente: su encuesta F4 o, si no la tiene, «Sin encuesta». */
type EntradaF4 = { tipo: 'encuesta'; f4: EncuestaF4 } | { tipo: 'sin'; cliente: SinEncuestaF4 };

const rolDe = (e: EntradaF4) => (e.tipo === 'encuesta' ? e.f4.rol : e.cliente.rol);
const claveOperacion = (idOperacion?: number | null, ccl?: string | null, idF4?: number) => {
  if (idOperacion != null) return `op:${idOperacion}`;
  if (ccl) return `ccl:${ccl}`;
  return `f4:${idF4}`;
};

/** Una operación (en Fase 4 o con alguna F4) con sus clientes por parte: lo que se pinta en una fila. */
interface OperacionF4 {
  clave: string;
  ccl?: string | null;
  nombreOperacion?: string | null;
  asesor?: string | null;
  tipoOperacion?: string | null;
  /** La F4 más reciente de la operación o, si no tiene ninguna, su entrada en Fase 4. */
  fecha?: string | null;
  vendedores: EntradaF4[];
  compradores: EntradaF4[];
  /** Cliente de gestión u otro rol: van con la parte vendedora. */
  otros: EntradaF4[];
}

/** La F4 en la forma que espera el modal de respuestas de «Registradas». */
const comoRegistrada = (f: EncuestaF4): EncuestaRegistrada => ({
  idEncuestaRealizada: f.id,
  fechaEncuesta: f.fechaCreacion,
  fechaRespuesta: f.fechaRespuesta,
  idEncuesta: 0,
  nombreEncuesta: 'F4 · Encuesta de Mystery',
  tipo: 'F4',
  idCliente: 0,
  codigoCliente: f.codigoCliente,
  nombreCliente: f.nombreCliente,
  ccl: f.ccl,
  nombreOperacion: f.nombreOperacion,
  rol: f.rol,
  respondida: true,
  numRespuestas: 0,
  visibleMystery: f.visibleMystery,
  quitadaManual: f.quitadaManual,
});

const fecha = (iso?: string | null) => (iso ? dayjs(iso).format('DD/MM/YYYY') : '—');

interface Props {
  /** Título y selector de vista: van al principio de la barra. */
  cabecera: React.ReactNode;
  /** Texto del buscador (común a las vistas): nombre, código CL, CCL u operación. */
  busqueda: string;
  onBusqueda: (v: string) => void;
}

/**
 * «Mystery (F4)»: todas las encuestas F4 con lo que ve Mystery en su portal. CxCp quita o pone la
 * visibilidad de cada una; lo que se quita a mano queda marcado y no se reactiva solo.
 */
const MysteryF4Tab: React.FC<Props> = ({ cabecera, busqueda, onBusqueda }) => {
  const [filas, setFilas] = useState<EncuestaF4[]>([]);
  /** Clientes de operaciones en Fase 4 sin encuesta (si no se pudieron cargar, el listado sigue con las F4). */
  const [sinEncuesta, setSinEncuesta] = useState<SinEncuestaF4[]>([]);
  const [errorSinEncuesta, setErrorSinEncuesta] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<FiltroF4>('todas');
  /** F4 con la ficha abierta (se lee de `filas` para que el interruptor de la ficha y la tabla vayan a la par). */
  const [idFicha, setIdFicha] = useState<number | null>(null);
  /** F4 con respuesta cuyas respuestas se están viendo (el mismo modal que en «Registradas»). */
  const [respuestasDe, setRespuestasDe] = useState<EncuestaF4 | null>(null);
  const peticion = useRef(0);

  const cargar = useCallback(async () => {
    const id = ++peticion.current;
    setLoading(true);
    setError(null);
    setErrorSinEncuesta(null);
    const [f4, sin] = await Promise.allSettled([getEncuestasF4(), getF4SinEncuesta()]);
    if (id !== peticion.current) return;
    if (f4.status === 'fulfilled') setFilas(f4.value);
    else {
      setFilas([]);
      setError(mensajeError(f4.reason, 'las encuestas F4'));
    }
    if (sin.status === 'fulfilled') setSinEncuesta(sin.value);
    else {
      setSinEncuesta([]);
      setErrorSinEncuesta(mensajeError(sin.reason, 'las operaciones en Fase 4 sin encuesta'));
    }
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const buscadas = useMemo(
    () => filas.filter((f) => coincideBusqueda(busqueda, f.nombreCliente, f.codigoCliente, f.ccl, f.nombreOperacion, f.asesor)),
    [filas, busqueda],
  );
  const sinBuscadas = useMemo(
    () => sinEncuesta.filter((s) => coincideBusqueda(busqueda, s.nombreCliente, s.codigoCliente, s.ccl, s.nombreOperacion, s.asesor)),
    [sinEncuesta, busqueda],
  );
  // Contadores sobre lo que pasa el buscador; la tabla, además, por la opción elegida.
  const conteo = useMemo(
    () => ({
      ...Object.fromEntries(FILTROS_F4.map((o) => [o.valor, buscadas.filter(o.cumple).length])),
      sinencuesta: sinBuscadas.length,
    }) as Record<string, number>,
    [buscadas, sinBuscadas],
  );
  /** Lo que entra en la tabla con la opción elegida: «Todas» = F4 y sin encuesta; un estado de la F4, solo F4. */
  const visiblesEnTabla = useMemo<EntradaF4[]>(() => {
    const comoEntrada = (f: EncuestaF4): EntradaF4 => ({ tipo: 'encuesta', f4: f });
    const sinEntradas = sinBuscadas.map((c): EntradaF4 => ({ tipo: 'sin', cliente: c }));
    if (filtro === 'todas') return [...buscadas.map(comoEntrada), ...sinEntradas];
    if (filtro === 'sinencuesta') return sinEntradas;
    const elegida = FILTROS_F4.find((o) => o.valor === filtro);
    return (elegida ? buscadas.filter(elegida.cumple) : buscadas).map(comoEntrada);
  }, [buscadas, sinBuscadas, filtro]);
  const nEncuestas = visiblesEnTabla.filter((e) => e.tipo === 'encuesta').length;

  const opcion = (o: OpcionFiltroF4) => ({ value: o.valor, label: etiquetaFiltro(o.texto, conteo[o.valor] ?? 0, o.color) });

  const actualizarFila = (id: number, visible: boolean) => setFilas((fs) => fs.map((f) => (f.id === id
    ? { ...f, visibleMystery: visible, quitadaManual: visible ? false : true }
    : f)));

  /** Tras guardar la ficha: la fila se queda con lo que devuelve el backend (notas y plazo ya guardados). */
  const actualizarFicha = (ficha: FichaF4) => setFilas((fs) => fs.map((f) => (f.id === ficha.id
    ? {
      ...f, notasFct: ficha.notasFct, notasMystery: ficha.notasMystery, plazoInicio: ficha.plazoInicio, plazoFin: ficha.plazoFin,
    }
    : f)));

  const fichaAbierta = useMemo(() => (idFicha == null ? null : filas.find((f) => f.id === idFicha) ?? null), [filas, idFicha]);

  /**
   * Una fila por operación (como «Operaciones F4 - Encuestas» de AuraQuantic): todos sus clientes por parte, con
   * su F4 o «Sin encuesta». Las F4 sin operación van solas en su fila.
   */
  const operaciones = useMemo<OperacionF4[]>(() => {
    const porClave = new Map<string, OperacionF4>();
    visiblesEnTabla.forEach((e) => {
      const d = e.tipo === 'encuesta'
        ? { ...e.f4, clave: claveOperacion(e.f4.idOperacion, e.f4.ccl, e.f4.id), fecha: e.f4.fechaCreacion }
        : { ...e.cliente, clave: claveOperacion(e.cliente.idOperacion, e.cliente.ccl), fecha: e.cliente.fechaEntradaF4 };
      let op = porClave.get(d.clave);
      if (!op) {
        op = {
          clave: d.clave, ccl: d.ccl, nombreOperacion: d.nombreOperacion, asesor: d.asesor, tipoOperacion: d.tipoOperacion,
          fecha: d.fecha, vendedores: [], compradores: [], otros: [],
        };
        porClave.set(d.clave, op);
      }
      op.asesor = op.asesor || d.asesor;
      op.tipoOperacion = op.tipoOperacion || d.tipoOperacion;
      const rol = rolDe(e);
      if (rol === 'Vendedor') op.vendedores.push(e);
      else if (rol === 'Comprador') op.compradores.push(e);
      else op.otros.push(e);
    });
    // La fecha de la fila: la F4 más reciente; la entrada en Fase 4 solo si la operación no tiene ninguna F4.
    const masReciente = (fechas: (string | null | undefined)[]) => fechas.reduce<string | null>(
      (max, f) => (f && (!max || f > max) ? f : max), null);
    return Array.from(porClave.values()).map((op) => {
      const todas = [...op.vendedores, ...op.compradores, ...op.otros];
      const deF4 = todas.flatMap((x) => (x.tipo === 'encuesta' ? [x.f4.fechaCreacion] : []));
      const entradas = todas.flatMap((x) => (x.tipo === 'sin' ? [x.cliente.fechaEntradaF4] : []));
      return { ...op, fecha: deF4.length ? masReciente(deF4) : masReciente(entradas) };
    });
  }, [visiblesEnTabla]);

  /** Nombre del cliente, con su rol si no es vendedor ni comprador (p. ej. «Cliente de gestión»). */
  const cliente = (codigo?: string | null, nombre?: string | null, rol?: string | null) => (
    <div>
      <Text strong>{codigo || '—'}</Text>{' '}
      <Text ellipsis style={{ maxWidth: 220 }}>{nombre || 'Sin nombre'}</Text>
      {rol && rol !== 'Vendedor' && rol !== 'Comprador' && <Text type="secondary" style={{ fontSize: 12 }}> · {rol}</Text>}
    </div>
  );

  /** Celda de una parte (vendedor o comprador): una ficha por cliente, apiladas si hay varios. */
  const parte = (entradas: EntradaF4[]) => {
    if (entradas.length === 0) return <Text type="secondary">—</Text>;
    return (
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        {entradas.map((entrada) => {
          if (entrada.tipo === 'sin') {
            const c = entrada.cliente;
            return (
              <div key={`sin:${c.idOperacion}:${c.codigoCliente}:${c.rol}`}>
                {cliente(c.codigoCliente, c.nombreCliente, c.rol)}
                <Tooltip title="Operación en Fase 4 sin encuesta F4 para este cliente">
                  <span><FctEstado personalizado={{ etiqueta: 'Sin encuesta', familia: 'riesgo', icono: 'alerta' }} /></span>
                </Tooltip>
              </div>
            );
          }
          const f = entrada.f4;
          const e = estadoMystery(f.estadoCod);
          return (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                {cliente(f.codigoCliente, f.nombreCliente, f.rol)}
                <Space size={4} wrap>
                  <FctEstado personalizado={{ etiqueta: e.texto, familia: e.familia }} />
                  {f.llamadasSinExito > 0 && (
                    <Tooltip title="Llamadas sin éxito de Mystery (a la sexta se envía la encuesta online)">
                      <Text type="secondary">{f.llamadasSinExito}/6</Text>
                    </Tooltip>
                  )}
                  <Text type="secondary" style={{ fontSize: 12 }}>plazo {fecha(f.plazoFin)}</Text>
                </Space>
              </div>
              {/* «Notas y plazo» (la ficha de CxCp) al lado del interruptor de visibilidad. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 'none' }}>
                {(f.estadoCod === 956 || !!f.fechaRespuesta) && (
                  <Tooltip title="Ver las respuestas de la encuesta">
                    <Button
                      type="text"
                      size="small"
                      icon={<FileSearchOutlined />}
                      aria-label="Ver respuestas"
                      onClick={() => setRespuestasDe(f)}
                    />
                  </Tooltip>
                )}
                <BotonFichaF4 encuesta={f} onAbrir={() => setIdFicha(f.id)} />
                <VisibilidadF4Switch
                  id={f.id}
                  visible={f.visibleMystery}
                  quitadaManual={f.quitadaManual}
                  onCambiado={(v) => actualizarFila(f.id, v)}
                />
              </div>
            </div>
          );
        })}
      </Space>
    );
  };

  /** Cabecera de una parte: su nombre y, a la derecha (encima de los interruptores), «Mystery». */
  const tituloParte = (parteNombre: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <span>{parteNombre}</span>
      <Tooltip title="Visible para Mystery en su portal">
        <span>Mystery</span>
      </Tooltip>
    </div>
  );

  const columnas: ColumnsType<OperacionF4> = [
    {
      title: <Tooltip title="Creación de la F4 más reciente o, si la operación no tiene ninguna, su entrada en Fase 4 (firma)">Fecha</Tooltip>,
      dataIndex: 'fecha',
      width: 105,
      render: (v?: string | null) => fecha(v),
      sorter: (a, b) => (a.fecha ?? '').localeCompare(b.fecha ?? ''),
      defaultSortOrder: 'descend',
    },
    {
      title: 'CCL',
      dataIndex: 'ccl',
      width: 150,
      render: (v?: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      // Como en AuraQuantic: el nombre, el asesor y el tipo (venta / gestión).
      title: 'Operación',
      dataIndex: 'nombreOperacion',
      width: 210,
      render: (v: string | null | undefined, op) => (
        <div style={{ minWidth: 0 }}>
          <Text strong ellipsis style={{ display: 'block' }}>{v || '—'}</Text>
          {op.asesor && <Text ellipsis style={{ display: 'block', fontSize: 12 }}>{op.asesor}</Text>}
          {op.tipoOperacion && <Text type="secondary" style={{ fontSize: 12 }}>{op.tipoOperacion.toLowerCase()}</Text>}
        </div>
      ),
    },
    {
      title: tituloParte('Vendedor · gestión'),
      key: 'vendedor',
      render: (_: unknown, op) => parte([...op.vendedores, ...(op.compradores.length || op.vendedores.length ? [] : op.otros)]),
    },
    {
      title: tituloParte('Comprador'),
      key: 'comprador',
      render: (_: unknown, op) => parte([...op.compradores, ...(op.compradores.length || op.vendedores.length ? op.otros : [])]),
    },
  ];

  return (
    <>
      <BarraLista
        cabecera={cabecera}
        acciones={
          <Tooltip title="Abre el portal de Mystery con tu cuenta de Farmaconsulting: ves y guardas lo mismo que Mystery">
            <Button icon={<ExportOutlined />} href={urlPortalMystery()} target="_blank" rel="noopener noreferrer">
              Abrir portal Mystery
            </Button>
          </Tooltip>
        }
        filtros={
          <>
            <Select<FiltroF4>
              value={filtro}
              onChange={setFiltro}
              prefix={<Text type="secondary">Estado</Text>}
              aria-label="Filtrar por estado"
              popupMatchSelectWidth={false}
              // Todas las opciones a la vista, sin lista virtual (la rueda del ratón no la movía bien).
              listHeight={480}
              virtual={false}
              style={{ width: 290 }}
              options={[
                { value: 'todas', label: etiquetaFiltro('Todas', buscadas.length + sinBuscadas.length, fct.marca['verde-base']) },
                {
                  label: 'Estado de la encuesta',
                  title: 'Estado de la encuesta',
                  options: [
                    { value: 'sinencuesta', label: etiquetaFiltro('Sin encuesta', conteo.sinencuesta ?? 0, fct.estado.riesgo.linea) },
                    ...POR_ESTADO.map(opcion),
                  ],
                },
                { label: 'Portal de Mystery', title: 'Portal de Mystery', options: POR_VISIBILIDAD.map(opcion) },
              ]}
            />
          </>
        }
      >
        <BuscadorCliente value={busqueda} onChange={onBusqueda} placeholder="Buscar: nombre, CL, CCL u operación" />
        <BotonActualizar onClick={cargar} loading={loading} />
      </BarraLista>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error}
          action={<Button size="small" onClick={cargar}>Reintentar</Button>}
        />
      )}
      {!error && errorSinEncuesta && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={`${errorSinEncuesta} Se ven solo las operaciones con F4.`} />
      )}

      <Table<OperacionF4>
        rowKey="clave"
        columns={columnas}
        dataSource={operaciones}
        loading={loading}
        size="small"
// Con la tabla vacía, sin barra de desplazamiento horizontal (se veía una barra suelta bajo «No hay…»).
        scroll={operaciones.length ? { x: 1100 } : undefined}
        locale={{
          emptyText: error
            ? <Empty description="No se han podido cargar las encuestas F4" />
            : <Empty description="No hay encuestas F4 con este filtro" />,
        }}
        pagination={{
          defaultPageSize: 50,
          pageSizeOptions: [25, 50, 100, 200],
          showSizeChanger: true,
          showTotal: (t) => `${t} operaciones · ${nEncuestas} encuestas F4 · ${visiblesEnTabla.length - nEncuestas} sin encuesta`,
        }}
      />

      <RespuestasModal encuesta={respuestasDe ? comoRegistrada(respuestasDe) : null} onClose={() => setRespuestasDe(null)} />

      <FichaF4Drawer
        encuesta={fichaAbierta}
        onCerrar={() => setIdFicha(null)}
        onGuardada={actualizarFicha}
        onVisibilidad={actualizarFila}
      />
    </>
  );
};

export default MysteryF4Tab;
